// @flow

import React from 'react';
import { render } from 'react-dom';
import { Router, match as matchRoutes, browserHistory } from 'react-router';
import { Provider } from 'react-redux';
import Promise from 'bluebird';
import { trigger } from 'redial';
import lowercaseKeys from 'lowercase-keys';
import omit from 'lodash.omit';
import { stringify as stringifyQueryString } from 'query-string';
import { parse as parseCookies } from 'cookie';
import createStore from 'store/createStore';
import { createAnalytics } from '@seek/seek-jobs-analytics';
import { locationChanged } from 'store/location/location';
import { environment } from 'config';
import routes from './routes';
import { startTransaction, endTransaction } from 'redux-batched-subscribe-transaction';
import { getTransaction } from 'store/transactions';

// Used to calculate 'routeEnter' values for each hook:
let prevRouteHandlers = [];

const fetchDataForRoutes = ({ renderProps, dispatch, authenticated, history }) => {
  // Keep a local copy of the previous route handlers array.
  // This is so the 'prevRouteHandlers' array can be safely updated mid-lifecycle.
  const prevRouteHandlersCopy = [ ...prevRouteHandlers ];

  const lowercasedQuery = lowercaseKeys(renderProps.location.query);

  const handleIntent = (intentName, callback) => {
    const value = lowercasedQuery[intentName];

    if (typeof value === 'undefined') {
      return;
    }

    const currentPath = renderProps.location.pathname;
    const cleanedQuery = omit(lowercasedQuery, intentName);

    history.replaceState({ silent: true }, currentPath, cleanedQuery);
    callback(value);
  };

  const getLocals = routeHandler => ({
    routeEnter: (prevRouteHandlersCopy.indexOf(routeHandler) === -1),
    path: renderProps.location.pathname,
    query: lowercasedQuery,
    state: renderProps.location.state || {},
    locale: /\.co\.nz/g.test(window.location.hostname) ? 'NZ' : 'AU',
    cookies: parseCookies(document.cookie),
    authenticated,
    handleIntent,
    dispatch
  });

  const routeHandlers = renderProps.routes.map(route => route.component);
  prevRouteHandlers = routeHandlers;

  const makeTrigger = name => () => trigger(name, routeHandlers, getLocals);

  let triggerFetch = () => Promise.resolve();
  let triggerSeo = () => Promise.resolve();
  let triggerIntents = () => Promise.resolve();

  if (window.SEEK_REDUX_DATA) {
    delete window.SEEK_REDUX_DATA;
    triggerIntents = makeTrigger('intents');
  } else {
    triggerFetch = makeTrigger('fetch');
    triggerSeo = makeTrigger('seo');
  }

  const triggerGetClientToken = makeTrigger('getClientToken');
  const triggerAuthenticated = makeTrigger('authenticated');
  const triggerDefer = makeTrigger('defer');
  const triggerMashup = makeTrigger('mashup');
  const triggerPageLoad = makeTrigger('pageLoad');

  const triggerAuthFlow = () => triggerGetClientToken()
    .then(() => Promise.all([
      triggerAuthenticated(),
      triggerIntents()
    ]))
    .catch(() => {}); // Swallow auth failures

  const triggerServerFlow = () => triggerFetch()
    .then(triggerSeo);

  const routeChangeTransaction = getTransaction('routeChange');
  dispatch(startTransaction(routeChangeTransaction));
  Promise.join(authenticated && triggerAuthFlow(), triggerServerFlow(), triggerDefer())
    .then(triggerMashup)
    .catch(Promise.CancellationError, () => {})
    .finally(triggerPageLoad)
    .then(() => dispatch(endTransaction(routeChangeTransaction)));
};

const ignoreFirstCall = fn => {
  let isFirstCall = true;

  return (...args) => {
    if (isFirstCall) {
      isFirstCall = false;
      return;
    }

    fn(...args);
  };
};

const hotjarVirtualPageView = ignoreFirstCall(({ pathname, query }) => {
  if (typeof window.hj === 'function') {
    const stringifiedQuery = stringifyQueryString(query);
    const queryString = stringifiedQuery ? `?${stringifiedQuery}` : '';
    window.hj('vpv', `${pathname}${queryString}`);
  }
});

export default (element:any) => {
  const analyticsData = window.SEEK_ANALYTICS_DATA || {};
  const analytics = createAnalytics(analyticsData, { environment });
  const store = createStore(analytics, window.SEEK_REDUX_DATA);
  const { dispatch } = store;

  if (window.SEEK_ANALYTICS_DATA) {
    delete window.SEEK_ANALYTICS_DATA;
  }

  browserHistory.listen(location => {
    if (location.state && location.state.silent) {
      return;
    }

    matchRoutes({ routes, location }, (routerError, redirectLocation, renderProps) => {
      const authenticated = store.getState().user.authenticated;
      dispatch(locationChanged(window.document.location.href, renderProps.location));
      fetchDataForRoutes({ renderProps, dispatch, authenticated, history: browserHistory });
      hotjarVirtualPageView(renderProps.location);
    });
  });

  render((
    <Provider store={store}>
      <Router history={browserHistory} routes={routes} />
    </Provider>
  ), element);
};



/** WEBPACK FOOTER **
 ** ./app/client-render.js
 **/